import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import { Chip, Tooltip } from '@mui/material';
import { isNull, isUndefined } from 'lodash';
import { F2_KEY } from 'common/constants/general';
import { GET_ENTER_DATA } from 'common/constants/inputs';
import { LedgerContext } from 'context';
import { formatNumbers, toString } from 'utilities';
import { CurrencyAdjustedValueViewerProps } from './CurrencyAdjustedValueViewer.props';
import { classes, Root } from './CurrencyAdjustedValueViewer.styles';
import { CurrencyAdjustedCell, LedgerContextType } from './CurrencyAdjustedValueViewer.types';

const buttonStyle = {
  background: 'none',
  border: 'none',
  color: 'inherit',
  width: '100%',
  textAlign: 'right' as const,
};

function checkEnterMessageConditions(cell: CurrencyAdjustedCell): boolean {
  return (((!isUndefined(cell.value) && !cell.hidden) || cell.value)
    && cell.gridType
    && (cell.gridType === 'number' || cell.gridType === 'percentage')
    && !Number.isNaN(cell.value)) as boolean;
}

const CurrencyAdjustedValueViewer = ({ cell, selected, onDoubleClick }: CurrencyAdjustedValueViewerProps) => {
  const { exchangeRateMap } = useContext(LedgerContext) as LedgerContextType;
  const [displayCurrency, setDisplayCurrency] = useState<string>('');
  const [isChipClicked, setIsChipClicked] = useState<boolean>(false);
  const [isChipVisible, setIsChipVisible] = useState<boolean>(false);

  const onChipClick = useCallback(() => {
    setIsChipClicked(prevState => !prevState);
  }, []);

  useEffect(() => {
    if (isChipClicked) {
      setDisplayCurrency(exchangeRateMap.fundCurrency);
    } else {
      setDisplayCurrency(cell.currency?.code ?? '');
    }
  }, [isChipClicked, exchangeRateMap, cell]);

  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key ?? (event as any).keyCode;
      if (selected && key && key.toUpperCase() === F2_KEY) {
        onDoubleClick?.();
      }
    },
    [onDoubleClick, selected]
  );

  const spanId = useMemo(() => {
    if (cell.extraAttr) {
      return `${cell.extraAttr}-${cell.alias}-${cell.key}_viewer`;
    }

    return `${cell.alias}-${cell.key}_viewer`;
  }, [cell]);

  useEffect(() => {
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [keyUpHandler, selected]);

  const value = useMemo(() => {
    if (checkEnterMessageConditions(cell)) {
      if (!cell.readOnly && !toString(cell.value).length) {
        return cell.enterDataMessage ?? GET_ENTER_DATA(cell.isRequired ?? false, cell.enterDataMessage ?? null);
      }
      // Override number of decimals and display the entire number to highlight the errors
      if (cell.allowDecimals === false && cell.tooltipMessages?.length && cell.tooltipMessages.length > 0) {
        return cell.value;
      }
      if (!isChipClicked) {
        return formatNumbers(cell as any);
      }
      const { relatedDate } = cell;
      const exchangeRate = relatedDate ? exchangeRateMap[relatedDate] : 1;
      return formatNumbers({
        ...cell,
        value: (cell.value as number) * (exchangeRateMap[cell.relatedDate as keyof typeof exchangeRateMap] as number),
        currency: {
          ...cell.currency,
          code: exchangeRateMap.fundCurrency,
        },
      } as any);
    }
    if (!cell.readOnly && !cell.hidden && !cell.value && cell.placeholder) {
      return cell.placeholder;
    }

    return cell.value;
  }, [cell, exchangeRateMap, isChipClicked]);

  const exchangeRateTooltip = useMemo(() => {
    if (isNull(cell.relatedDate)) {
      return 'Please select a date to see the exchange rate';
    }
    if (isUndefined(exchangeRateMap?.[cell.relatedDate!])) {
      return 'Loading exchange rate';
    }
    if (exchangeRateMap?.[cell.relatedDate!] === -1) {
      return 'Oops! An error occurred loading the exchange rate.';
    }
    return `1 ${cell.currency?.code} -> ${exchangeRateMap[cell.relatedDate!]} ${exchangeRateMap.fundCurrency}`;
  }, [exchangeRateMap, cell]);

  const showChip = useCallback(() => {
    setIsChipVisible(true);
  }, []);

  const hideChip = useCallback(() => {
    setIsChipVisible(false);
  }, []);

  const isChipDisabled = useMemo(
    () =>
      isNull(cell.relatedDate)
      ?? isUndefined(exchangeRateMap?.[cell.relatedDate!])
      ?? exchangeRateMap?.[cell.relatedDate!] === -1,
    [exchangeRateMap, cell]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Prevent the default action to stop scrolling when space is pressed
      e.preventDefault();
      showChip();
    }
  };

  return (
    <Root
      type="button"
      tabIndex={0}
      onMouseOver={showChip}
      onFocus={showChip}
      onMouseLeave={hideChip}
      onKeyDown={handleKeyDown}
      style={buttonStyle}>
      <span id={spanId} className={`text-cell ${cell.className || ''}`}>
        {displayCurrency && isChipVisible && (
          <div className={classes.chipContainer} style={{ cursor: 'pointer' }}>
            <Tooltip title={exchangeRateTooltip} placement="top">
              <span>
                <Chip
                  label={displayCurrency}
                  size="small"
                  color="secondary"
                  disabled={isChipDisabled}
                  onClick={onChipClick}
                />
              </span>
            </Tooltip>
          </div>
        )}
        {value}
      </span>
    </Root>
  );
};

export default CurrencyAdjustedValueViewer;
